import type React from 'react';
import styled from 'styled-components';
import Typography from 'app/ui/components/atoms/typography';
import type { CarouselSlide } from 'app/ui/components/molecules/carousel-modal/types';

interface SlideProps {
  slide: CarouselSlide;
  buttons?: React.ReactNode;
}

const SlideContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
  
  ${({ theme: { media } }) => `
    ${media.large(
      () => `
        flex-direction: row;
      `,
    )}
  `}
`;

const ImageSection = styled.div`
  flex: 0 0 300px;
  position: relative;
  overflow: hidden;
  touch-action: pan-x;
  
  ${({ theme: { media } }) => `
  ${media.medium(
    () => `
        flex: 0 0 600px;
      `,
  )}
    ${media.large(
      () => `
        flex: 1;
        min-height: 60vh;
      `,
    )}
  `}
`;

const SlideImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  position: absolute;
  top: 0;
  left: 0;
`;

const ContentSection = styled.div`
  flex: 1;
  background-color: ${({ theme: { colors } }) => colors.darkGrey};
  padding: ${({ theme: { spacing } }) => spacing(8)};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
  max-height: calc(80vh - 300px);
  
  ${({ theme: { media, spacing } }) => `
    ${media.medium(
      () => `
        padding: ${spacing(12)} ${spacing(6)} ${spacing(6)} ${spacing(10)};
        max-height: calc(80vh - 600px);
      `,
    )}
    ${media.large(
      () => `
        max-height: none;
      `,
    )}
  `}
`;

const TextContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  touch-action: pan-y;
  
  ${({ theme: { media } }) => `
    ${media.medium(
      () => `
        justify-content: center;
        overflow: visible;
        touch-action: auto;
      `,
    )}
  `}
`;

const ButtonSection = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: ${({ theme: { spacing } }) => spacing(6)};
  flex-shrink: 0;
  touch-action: pan-x;
  
  ${({ theme: { media } }) => `
    ${media.medium(
      () => `
        width: auto;
        align-self: flex-end;
      `,
    )}
  `}
`;

const Title = styled(Typography)`
  margin-bottom: ${({ theme: { spacing } }) => spacing(4)};
`;

export const Slide: React.FC<SlideProps> = ({ slide, buttons }) => {
  return (
    <SlideContainer>
      <ImageSection>
        <SlideImage src={slide.imageUrl} alt={slide.title} />
      </ImageSection>
      <ContentSection>
        <TextContent>
          <Title variant="h4" color="white">
            {slide.title}
          </Title>
          <Typography variant="body2">{slide.description}</Typography>
        </TextContent>
        {buttons && <ButtonSection>{buttons}</ButtonSection>}
      </ContentSection>
    </SlideContainer>
  );
};
